import { useFieldContext } from '@/lib/forms/context'
import { cn } from '@/lib/utils'

import { Field, FieldError, FieldLabel } from '../ui/field'
import { Input } from '../ui/input'

type Props = {
  label: string
  placeholder?: string
  type?: string
  className?: string
  formatter?: (val: string) => string
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search'
  autoFocus?: boolean
}

export const TextField = ({
  label,
  placeholder,
  type,
  className,
  formatter,
  inputMode,
  autoFocus,
}: Props) => {
  const field = useFieldContext<string | number>()

  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

  return (
    <Field data-invalid={isInvalid}>
      <FieldLabel
        htmlFor={field.name}
        className="text-foreground text-base font-bold"
      >
        {label}
      </FieldLabel>
      <Input
        id={field.name}
        name={field.name}
        type={type}
        value={field.state.value}
        onBlur={field.handleBlur}
        onChange={(e) => {
          let val = e.target.value
          if (type === 'number') {
            // Allow digits, one decimal point, and leading minus
            const filteredVal = val.replace(/[^0-9.-]/g, '')

            // Basic numeric validation
            if (val !== filteredVal) {
              val = filteredVal
            }

            if (formatter) {
              val = formatter(val)
            }

            if (val === '') {
              // Use undefined for empty numeric fields to avoid NaN and let Zod handle validation
              field.handleChange(undefined as any)
              return
            }

            const num = Number(val)
            if (!isNaN(num)) {
              field.handleChange(num)
            }
          } else {
            if (formatter) {
              val = formatter(val)
            }
            field.handleChange(val)
          }
        }}
        aria-invalid={isInvalid}
        placeholder={placeholder}
        inputMode={inputMode}
        autoFocus={autoFocus}
        className={cn(
          'border-muted focus-visible:ring-primary h-12 bg-white px-4',
          className,
        )}
      />
      {isInvalid && <FieldError errors={field.state.meta.errors} />}
    </Field>
  )
}
